import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { saveBooking } from '../data/bookingStorage';
import StepIndicator from '../components/StepIndicator';
import { formatDate } from '../data/dateLocale';
import { CreditCard, Lock, ChevronDown, Check } from 'lucide-react';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { selectedTrip, selectedReturnTrip, searchParams, passengerInfo, setCurrentStep, totalPrice, seatAssignments } = useBooking();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const bookingSaved = useRef(false);

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardName, setCardName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(720);
  const [selectedBank, setSelectedBank] = useState(null);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');

  useEffect(() => { setCurrentStep(4); }, [setCurrentStep]);
  useEffect(() => {
    if (timer <= 0 || success) return;
    const interval = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer, success]);

  useEffect(() => {
    if (!selectedTrip) navigate('/');
  }, [selectedTrip, navigate]);

  if (!selectedTrip) return null;

  const formatCardNumber = (val) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 16);
    return cleaned.replace(/(.{4})/g, '$1 ').trim();
  };
  const formatExpiry = (val) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 4);
    if (cleaned.length >= 3) return cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    return cleaned;
  };
  const doSaveBooking = () => {
    if (bookingSaved.current) return;
    bookingSaved.current = true;
    saveBooking({
      userEmail: user?.email || passengerInfo.email,
      trip: selectedTrip,
      returnTrip: selectedReturnTrip,
      departureDate: searchParams.departureDate,
      returnDate: searchParams.returnDate,
      passengers: searchParams.passengers,
      passengerInfo,
      seatAssignments,
      totalPrice: totalPrice(),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setSuccess(true); doSaveBooking(); }, 2000);
  };

  const handleBankPayment = (bank) => {
    setSelectedBank(bank);
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setSuccess(true); doSaveBooking(); }, 2500);
  };

  const handleBusinessLogin = () => {
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setSuccess(true); doSaveBooking(); }, 2000);
  };

  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;

  if (success) {
    const L = (obj) => obj[lang] || obj.ru || obj.en;
    return (
      <div className="min-h-screen bg-[#f0f0f1] py-10 px-4 flex items-start md:items-center justify-center">
        <div className="bg-white rounded-2xl p-6 md:p-10 max-w-[560px] w-full shadow-xl">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#2e9e5a]/10 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-[#2e9e5a] flex items-center justify-center">
                <Check size={32} className="text-white" strokeWidth={3} />
              </div>
            </div>
            <h1 className="text-[26px] md:text-[30px] font-bold text-[#222] mb-2">
              {L({ ru: 'Бронирование подтверждено', en: 'Booking confirmed', lv: 'Rezervācija apstiprināta', lt: 'Rezervacija patvirtinta', pl: 'Rezerwacja potwierdzona' })}
            </h1>
            <p className="text-[15px] text-[#555] mb-1">
              {L({ ru: 'Ваш билет успешно оформлен', en: 'Your ticket has been issued', lv: 'Jūsu biļete ir izrakstīta', lt: 'Jūsų bilietas išrašytas', pl: 'Twój bilet został wystawiony' })}
            </p>
            <p className="text-[13px] text-[#888] mb-6">
              {L({ ru: 'Покажите его при посадке', en: 'Show it when boarding', lv: 'Parādiet to iekāpjot', lt: 'Parodykite įlipdami', pl: 'Pokaż przy wsiadaniu' })}
            </p>
          </div>

          <div className="bg-[#f8f8f8] rounded-xl p-5 mb-5 border border-gray-100">
            <div className="text-[18px] font-bold text-[#222] mb-1">{selectedTrip.from} → {selectedTrip.to}</div>
            <div className="text-[12px] text-[#777] mb-4">{L({ ru: 'Автовокзал → Автовокзал', en: 'Bus station → Bus station', lv: 'Autoosta → Autoosta', lt: 'Autobusų stotis → Autobusų stotis', pl: 'Dworzec → Dworzec' })}</div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-[#888] mb-0.5">{L({ ru: 'Дата', en: 'Date', lv: 'Datums', lt: 'Data', pl: 'Data' })}</div>
                <div className="text-[14px] font-semibold text-[#333]">{formatDate(searchParams.departureDate, lang)}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-[#888] mb-0.5">{L({ ru: 'Время', en: 'Time', lv: 'Laiks', lt: 'Laikas', pl: 'Czas' })}</div>
                <div className="text-[14px] font-semibold text-[#333]">{selectedTrip.departureTime} — {selectedTrip.arrivalTime}</div>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 pb-3 border-b border-gray-200">
              <span className="text-[13px] text-[#555]">{L({ ru: 'Пассажир', en: 'Passenger', lv: 'Pasažieris', lt: 'Keleivis', pl: 'Pasażer' })}</span>
              <span className="text-[14px] font-semibold text-[#333]">{passengerInfo.firstName} {passengerInfo.lastName}</span>
            </div>
            <div className="flex justify-between items-center pt-3">
              <span className="text-[14px] font-bold text-[#222]">{L({ ru: 'Итого', en: 'Total', lv: 'Kopā', lt: 'Iš viso', pl: 'Razem' })}</span>
              <span className="text-[20px] font-bold text-[#c30909]">{totalPrice()} €</span>
            </div>
          </div>

          <div className="bg-[#fff8e6] border border-[#f0d878] rounded-xl p-4 mb-5">
            <div className="text-[13px] font-bold text-[#7a5a00] mb-2">
              {L({ ru: 'Что дальше:', en: 'What\'s next:', lv: 'Kas tālāk:', lt: 'Kas toliau:', pl: 'Co dalej:' })}
            </div>
            <ul className="space-y-1.5 text-[13px] text-[#5a4500]">
              <li>• {L({ ru: 'Прибудьте на автовокзал за 10–15 минут до отправления', en: 'Arrive at the station 10–15 minutes before departure' })}</li>
              <li>• {L({ ru: 'Подготовьте билет на экране телефона', en: 'Have the ticket ready on your phone screen' })}</li>
              <li>• {L({ ru: 'Сохраните билет или сделайте скриншот', en: 'Save the ticket or take a screenshot' })}</li>
            </ul>
          </div>

          <p className="text-center text-[12px] text-[#888] mb-5">
            ✓ {L({ ru: 'Билет сохранён в системе', en: 'Ticket saved in the system', lv: 'Biļete saglabāta sistēmā', lt: 'Bilietas išsaugotas sistemoje', pl: 'Bilet zapisany w systemie' })}
          </p>

          <div className="space-y-2">
            {user && (
              <button onClick={() => navigate('/account')} className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-sm px-8 py-3 rounded-lg transition-colors w-full uppercase tracking-wider">
                {t('account.myTrips')}
              </button>
            )}
            <button onClick={() => navigate('/')} className={`${user ? 'border border-gray-300 text-[#484c55] hover:bg-gray-50' : 'bg-[#c30909] hover:bg-[#a00707] text-white'} font-semibold text-sm px-8 py-3 rounded-lg transition-colors w-full uppercase tracking-wider`}>
              {t('success.backHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f0f1]">
      <StepIndicator />
      <div className="max-w-[1280px] mx-auto px-3 md:px-4 py-6 md:py-8">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-1">
            <h1 className="text-xl font-bold mb-6">{t('payment.title')}</h1>

            <div className="bg-white rounded-xl p-4 shadow-sm mb-4 flex items-center justify-end gap-2">
              <div className="flex items-center gap-1">
                <div className="bg-[#c30909] text-white text-sm font-bold w-8 h-8 flex items-center justify-center rounded">{String(minutes).padStart(2, '0')}</div>
                <div className="bg-[#c30909] text-white text-sm font-bold w-8 h-8 flex items-center justify-center rounded">{String(seconds).padStart(2, '0')}</div>
              </div>
              <span className="text-[10px] text-[#777] uppercase">{t('payment.minutes')} {t('payment.seconds')}</span>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="flex border-b">
                <div className="flex-1 py-3 text-sm font-medium text-[#484c55] text-center relative">
                  {t('payment.card')}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#c30909]" />
                </div>
              </div>

              <div className="p-6">
                {true ? (
                  <form onSubmit={handleSubmit}>
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-5 h-5 rounded-full border-2 border-[#c30909] flex items-center justify-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#c30909]" />
                      </div>
                      <CreditCard size={20} className="text-[#555]" />
                      <span className="text-sm font-medium">{t('payment.cardLabel')}</span>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.cardNumber')}</label>
                        <div className="relative">
                          <input type="text" required value={cardNumber} onChange={(e) => setCardNumber(formatCardNumber(e.target.value))} placeholder="1234 5678 9012 3456" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors pr-24" />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                            <svg width="34" height="22" viewBox="0 0 40 26" fill="none">
                              <circle cx="14" cy="13" r="10" fill="#eb001b"/>
                              <circle cx="26" cy="13" r="10" fill="#f79e1b" fillOpacity="0.9"/>
                            </svg>
                            <span className="font-bold text-[15px] tracking-wider text-[#1a1f71]" style={{ fontFamily: "'Inter', sans-serif" }}>VISA</span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.expiry')}</label>
                          <input type="text" required value={expiry} onChange={(e) => setExpiry(formatExpiry(e.target.value))} placeholder="MM/YY" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.cvv')}</label>
                          <div className="relative">
                            <input type="text" required value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder={t('payment.cvvPlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors pr-10" />
                            <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.cardName')}</label>
                        <input type="text" required value={cardName} onChange={(e) => setCardName(e.target.value)} placeholder={t('payment.cardNamePlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors" />
                      </div>
                      <button type="submit" disabled={processing} className="w-full bg-[#c30909] hover:bg-[#a00707] disabled:bg-[#c30909]/50 text-white font-semibold text-sm uppercase tracking-wider py-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                        {processing ? (
                          <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t('payment.processing')}</>
                        ) : (
                          <><Lock size={14} />{t('payment.pay')} {totalPrice()} €</>
                        )}
                      </button>
                    </div>
                  </form>
                ) : null}
                <p className="text-[10px] text-[#484c55]/40 mt-4">ⓘ {t('payment.terms')}</p>
              </div>
            </div>
          </div>

          <div className="w-full md:w-[340px] flex-shrink-0">
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-4">
              <h2 className="text-lg font-bold mb-4">{t('summary.title')}</h2>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#c30909]" />
                  <span className="text-xs font-semibold text-[#777] uppercase">{t('summary.outbound')}</span>
                  <ChevronDown size={14} className="text-[#484c55]/30 ml-auto" />
                </div>
                <p className="text-sm font-bold">{formatDate(searchParams.departureDate, lang)}</p>
                <p className="text-xs text-[#555] mt-1">{selectedTrip.departureTime} {selectedTrip.from} — {selectedTrip.arrivalTime} {selectedTrip.to}</p>
              </div>
              {selectedReturnTrip && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[#484c55]/30" />
                    <span className="text-xs font-semibold text-[#777] uppercase">{t('summary.return')}</span>
                    <ChevronDown size={14} className="text-[#484c55]/30 ml-auto" />
                  </div>
                  <p className="text-sm font-bold">{formatDate(searchParams.returnDate, lang)}</p>
                  <p className="text-xs text-[#555] mt-1">{selectedReturnTrip.departureTime} {selectedReturnTrip.from} — {selectedReturnTrip.arrivalTime} {selectedReturnTrip.to}</p>
                </div>
              )}
              <div className="border-t pt-4 mt-4">
                <button
                  onClick={() => setVoucherOpen(!voucherOpen)}
                  className="text-[#c30909] text-xs font-semibold uppercase tracking-wider mb-3 hover:underline"
                >
                  {t('payment.addVoucher')}
                </button>
                {voucherOpen && (
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value)}
                      placeholder={t('search.enterCode')}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#c30909]"
                    />
                    <button
                      onClick={() => { setVoucherCode(''); setVoucherOpen(false); }}
                      className="bg-[#c30909] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#a00707] transition-colors"
                    >
                      OK
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#555]">{t('summary.passenger')} × {Object.values(searchParams.passengers).reduce((a, b) => a + b, 0)}</span>
                  <span className="text-sm font-medium">{totalPrice()} €</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-base font-bold">{t('summary.total')}</span>
                  <span className="text-xl font-bold">{totalPrice()} €</span>
                </div>
              </div>
              <p className="text-[10px] text-[#484c55]/40 mt-4 leading-relaxed">ⓘ {t('payment.corpDiscount')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
